/**
 * Partial baskets — ROADMAP 7.9 §6, on the screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SENTENCE IS THE FEATURE
 *
 * "1 leg failed" is true and useless. What a trader needs to know is whether
 * the thing they are now holding is still the thing they designed, and for a
 * hedged structure the answer is often no — an Iron Condor missing its long
 * wing is a naked short strangle, and capped risk has become unbounded.
 *
 * So the panel is sized around the sentence, not around the leg table. The
 * legs are underneath it for the retry; the sentence is what gets read.
 *
 * TWO WEIGHTS, DELIBERATELY. A basket whose missing leg changed the risk is
 * red and cannot be missed. One whose missing leg did not is amber and
 * ordinary. Rendering both the same is how a badge stops being read by the
 * time the first real one appears.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react'
import { AlertTriangle, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TradeIssues, TradeLegView, TradeView } from './useTradeIssues'

export function IssuePanel({ issues }: { issues: TradeIssues }): React.JSX.Element | null {
  if (issues.state.kind === 'ERROR') {
    return (
      <section className="qbar-card p-3" data-testid="issue-panel">
        <p className="text-[11px] text-muted-foreground" data-testid="issue-error">
          Your trades could not be read: {issues.state.why}
        </p>
      </section>
    )
  }
  if (issues.issues.length === 0) return null

  return (
    <div className="space-y-2" data-testid="issue-panel">
      {issues.issues.map((t) => (
        <IssueCard key={t.tradeId} trade={t} onRetry={issues.retry} />
      ))}
    </div>
  )
}

function IssueCard({
  trade,
  onRetry,
}: {
  trade: TradeView
  onRetry: (tradeLegId: string) => Promise<void>
}): React.JSX.Element {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The emergency, per §6: the failure CHANGED the risk and left it open-ended.
  const severe = trade.risk?.unbounded === true && trade.risk.worseThanIntended

  const retry = async (legId: string): Promise<void> => {
    setBusy(legId)
    setError(null)
    try {
      await onRetry(legId)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'The retry could not be sent.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section
      data-testid={`issue-${trade.tradeId}`}
      className={cn(
        'qbar-card p-3',
        severe ? 'border-destructive/50 bg-destructive/[0.05]' : 'border-warning/40 bg-warning/[0.04]',
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className={cn('mt-0.5 shrink-0', severe ? 'text-destructive' : 'text-warning')} />
        <div className="min-w-0 flex-1">
          <h2
            className={cn('text-[12px] font-extrabold', severe ? 'text-destructive' : 'text-warning')}
            data-testid={`issue-sentence-${trade.tradeId}`}
          >
            {/* Straight from the server. See the hook for why it is not
                recomputed here. */}
            {trade.risk?.sentence ?? 'A leg of this basket did not open'}
          </h2>
          <p className="num mt-0.5 text-[10px] text-muted-foreground">
            {trade.underlying} · {trade.legs.length} leg{trade.legs.length === 1 ? '' : 's'} ·
            opened {new Date(trade.openedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
      </div>

      <table className="num mt-2.5 w-full text-[11px]">
        <tbody>
          {trade.legs.map((l) => (
            <LegRow
              key={l.tradeLegId}
              leg={l}
              busy={busy === l.tradeLegId}
              anyBusy={busy !== null}
              onRetry={() => void retry(l.tradeLegId)}
            />
          ))}
        </tbody>
      </table>

      {error !== null && (
        <p className="mt-2 text-[10px] text-destructive" data-testid={`issue-error-${trade.tradeId}`}>
          {error}
        </p>
      )}
    </section>
  )
}

function LegRow({
  leg,
  busy,
  anyBusy,
  onRetry,
}: {
  leg: TradeLegView
  busy: boolean
  anyBusy: boolean
  onRetry: () => void
}): React.JSX.Element {
  const failed = leg.status === 'FAILED'

  return (
    <tr
      className={cn('border-b border-border/40', failed && 'opacity-100')}
      data-testid={`issue-leg-${leg.tradeLegId}`}
    >
      <td className="py-1.5">
        <span className={leg.side === 'SELL' ? 'text-destructive' : 'text-success'}>{leg.side}</span>
      </td>
      <td className={leg.optionType === 'CE' ? 'text-primary' : 'text-warning'}>
        {leg.strike.toLocaleString('en-IN')} {leg.optionType}
      </td>
      {/* UNITS, not lots: a lot count is a sizing instruction; a quantity is
          what is held. The failed leg shows what it WOULD have been. */}
      <td className="text-right text-muted-foreground">
        {failed ? `${leg.qty.toLocaleString('en-IN')} intended` : Math.abs(leg.netQty).toLocaleString('en-IN')}
      </td>
      <td className="text-right text-muted-foreground">
        {failed ? '—' : `@${leg.avgPrice.toFixed(2)}`}
      </td>
      <td className="pl-2">
        <span
          className={cn(
            'rounded px-1 py-0.5 text-[8px] font-extrabold',
            failed ? 'bg-destructive/15 text-destructive' : 'bg-success/15 text-success',
          )}
        >
          {leg.status}
        </span>
      </td>
      <td className="py-1 pl-2 text-right">
        {leg.retryable && (
          <button
            type="button"
            data-testid={`issue-retry-${leg.tradeLegId}`}
            onClick={onRetry}
            // Only one retry at a time per basket: two in flight on a
            // partially-hedged structure is how a second leg ends up doubled.
            disabled={anyBusy}
            title="Send this leg again, at the size it was placed with."
            className="inline-flex h-7 items-center gap-1 rounded-md bg-foreground px-2.5 text-[10px] font-extrabold text-background transition-all hover:opacity-85 disabled:opacity-30"
          >
            <RotateCw size={10} className={cn(busy && 'animate-spin')} />
            {busy ? 'SENDING…' : 'RETRY'}
          </button>
        )}
      </td>
    </tr>
  )
}
